import { useSubscriptionMe, useSubscriptionUsage } from '../libs/ohat-api';
import { useAuth } from './useAuth';
import { FeatureKey, isFlagFeature } from '../constants/features';

const STALE_TIME = 60 * 1000; // 1分キャッシュ

/**
 * 現在のプラン情報・使用量を提供し、機能ゲーティングの判定を行うフック。
 * 全メンバーが取得対象（owner限定にしない）。
 */
export function usePlan() {
  const { account } = useAuth();
  const enabled = !!account?.organizationId;

  const {
    data: planInfo,
    isLoading: isPlanLoading,
    error: planError,
    refetch: refetchPlan,
  } = useSubscriptionMe({
    query: { enabled, retry: false, staleTime: STALE_TIME },
  });

  const {
    data: usageRes,
    isLoading: isUsageLoading,
    refetch: refetchUsage,
  } = useSubscriptionUsage({
    query: { enabled, retry: false, staleTime: STALE_TIME },
  });

  const planKey = (planInfo?.planKey as 'free' | 'light' | undefined) ?? 'free';
  const isLight = planKey === 'light';
  const isFree = planKey === 'free';
  const usageItems = usageRes?.items ?? [];

  const getUsage = (featureKey: FeatureKey) =>
    usageItems.find((u) => u.featureKey === featureKey);

  /** 指定機能が利用可能か。フラグ系はライト、数量系は残数で判定。 */
  const canUse = (featureKey: FeatureKey): boolean => {
    if (isFlagFeature(featureKey)) {
      return isLight;
    }
    const usage = getUsage(featureKey);
    if (!usage) return isLight; // usage未取得時は保守的にプランで判定
    if (usage.limit === -1) return true; // 無制限
    return usage.current < usage.limit;
  };

  /** 数量系機能の残数（無制限は Infinity）。 */
  const getRemaining = (featureKey: FeatureKey): number => {
    const usage = getUsage(featureKey);
    if (!usage) return 0;
    if (usage.limit === -1) return Infinity;
    return Math.max(0, usage.limit - usage.current);
  };

  return {
    planInfo,
    planKey,
    isFree,
    isLight,
    isLoading: isPlanLoading || isUsageLoading,
    error: planError,
    usageItems,
    getUsage,
    canUse,
    getRemaining,
    refetch: () => {
      refetchPlan();
      refetchUsage();
    },
  };
}
